"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";

import { readErrorMessage } from "../lib/http";

const apiOrigin = process.env.NEXT_PUBLIC_API_ORIGIN ?? "";

type LoginErrors = {
  email?: string;
  password?: string;
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("Folio 계정으로 로그인해 주세요.");
  const [messageType, setMessageType] = useState<"info" | "error" | "success">("info");
  const [errors, setErrors] = useState<LoginErrors>({});
  const [isBusy, setIsBusy] = useState(false);

  function validateLogin() {
    const nextErrors: LoginErrors = {};
    if (!email.trim()) {
      nextErrors.email = "이메일을 입력해 주세요.";
    } else if (!email.includes("@")) {
      nextErrors.email = "올바른 이메일 형식으로 입력해 주세요.";
    }
    if (!password) {
      nextErrors.password = "비밀번호를 입력해 주세요.";
    } else if (password.length < 8) {
      nextErrors.password = "비밀번호는 8자 이상이어야 합니다.";
    }
    setErrors(nextErrors);
    return nextErrors;
  }

  async function submitLogin() {
    const nextErrors = validateLogin();
    if (Object.keys(nextErrors).length) {
      setMessageType("error");
      setMessage("입력 내용을 확인해 주세요.");
      return;
    }
    setIsBusy(true);
    setMessageType("info");
    setMessage("로그인을 처리하고 있습니다.");
    try {
      const response = await fetch(`${apiOrigin}/api/v1/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const data = (await response.json()) as { access_token: string };
      localStorage.setItem("folio_token", data.access_token);
      setMessageType("success");
      setMessage("로그인되었습니다. 대시보드로 이동합니다.");
      window.location.href = "/dashboard";
    } catch (error) {
      setMessageType("error");
      setMessage(error instanceof Error ? error.message : "로그인에 실패했습니다.");
    } finally {
      setIsBusy(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submitLogin();
  }

  return (
    <main className="grid min-h-screen place-items-center bg-[#f4f7fb] px-4 text-slate-950">
      <form
        action="/api/v1/auth/login/form"
        method="post"
        noValidate
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6"
      >
        <p className="text-sm font-semibold text-emerald-700">Folio</p>
        <h1 className="mt-2 text-2xl font-semibold">로그인</h1>
        <p
          className={`mt-4 rounded-md border px-3 py-2 text-sm font-medium ${
            messageType === "error"
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : messageType === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-200 bg-slate-50 text-slate-600"
          }`}
        >
          {message}
        </p>
        <div className="mt-6 grid gap-4">
          <label className="grid gap-2 text-sm font-medium">
            이메일
            <input
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setErrors((current) => ({ ...current, email: undefined }));
              }}
              className={`h-11 rounded-md border px-3 font-normal ${
                errors.email ? "border-rose-300 bg-rose-50" : "border-slate-300"
              }`}
              type="email"
              name="email"
              autoComplete="email"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? "login-email-error" : undefined}
              required
            />
            {errors.email ? (
              <span id="login-email-error" className="text-xs font-normal text-rose-700">
                {errors.email}
              </span>
            ) : null}
          </label>
          <label className="grid gap-2 text-sm font-medium">
            비밀번호
            <input
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setErrors((current) => ({ ...current, password: undefined }));
              }}
              className={`h-11 rounded-md border px-3 font-normal ${
                errors.password ? "border-rose-300 bg-rose-50" : "border-slate-300"
              }`}
              type="password"
              name="password"
              autoComplete="current-password"
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? "login-password-error" : undefined}
              required
            />
            {errors.password ? (
              <span id="login-password-error" className="text-xs font-normal text-rose-700">
                {errors.password}
              </span>
            ) : null}
          </label>
          <button
            type="submit"
            disabled={isBusy}
            className="h-11 rounded-md bg-slate-950 text-sm font-semibold text-white disabled:bg-slate-400"
          >
            {isBusy ? "로그인 처리 중..." : "로그인"}
          </button>
        </div>
        <p className="mt-5 text-sm text-slate-600">
          계정이 없나요?{" "}
          <Link href="/signup" className="font-semibold text-emerald-700">
            회원가입
          </Link>
        </p>
      </form>
    </main>
  );
}
